import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { submitContact } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '918983636311';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hi SŌHM! I have a question about your collectibles.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="contact-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-[0.1em] mb-4">
            Contact <span className="text-primary">Us</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Have questions about our collectibles? Want to track an order? We're here to help.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            {submitted ? (
              <div className="bg-primary/10 border border-primary/20 rounded-sm p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-display text-xl font-bold mb-2">Message Sent!</h3>
                <p className="text-muted-foreground mb-4">We'll get back to you within 24 hours.</p>
                <Button variant="outline" onClick={() => setSubmitted(false)}>
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card border border-border/40 rounded-sm p-6 space-y-4">
                <h2 className="font-display text-lg font-bold uppercase tracking-wide mb-4">Send a Message</h2>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-1 block">Name *</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-secondary/50 border-border"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-1 block">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-secondary/50 border-border"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-1 block">Subject</label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Order inquiry, General question..."
                    className="bg-secondary/50 border-border"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase mb-1 block">Message *</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help you?"
                    rows={4}
                    className="w-full bg-secondary/50 border border-border rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:border-primary/50"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground" data-testid="contact-submit-button">
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* WhatsApp */}
            <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-display font-bold">WhatsApp Support</h3>
                  <p className="text-sm text-muted-foreground">Quick responses, real-time help</p>
                </div>
              </div>
              <Button
                onClick={openWhatsApp}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat on WhatsApp
              </Button>
            </div>

            {/* Other Contact Info */}
            <div className="bg-card border border-border/40 rounded-sm p-6 space-y-4">
              <h3 className="font-display font-bold uppercase text-sm tracking-wide">Other Ways to Reach Us</h3>
              
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <a href="mailto:hello@sohm.com" className="text-sm text-primary hover:underline" data-testid="contact-email-link">
                    hello@sohm.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Phone</p>
                  <a href="tel:+918983636311" className="text-sm text-muted-foreground" data-testid="contact-phone-link">
                    +91 89836 36311
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Office</p>
                  <p className="text-sm text-muted-foreground">
                    Mumbai, Maharashtra, India
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-card border border-border/40 rounded-sm p-6">
              <h3 className="font-display font-bold uppercase text-sm tracking-wide mb-3">Business Hours</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monday - Friday</span>
                  <span>10:00 AM - 7:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Saturday</span>
                  <span>10:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sunday</span>
                  <span className="text-primary">Closed</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
